/**
 * CONSAI Business App - Stock/Inventory Management Page
 * Enhanced with summary boxes, activity log, and improved UX
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { GlowCard } from '../components/ui/glow-card';
import { toast } from 'sonner';
import {
  Package, Plus, Search, Edit2, Trash2, ArrowUp, ArrowDown,
  AlertTriangle, ChevronLeft, ChevronRight, Image as ImageIcon, X, Upload, Loader2,
  IndianRupee, TrendingUp, Layers, History, Eye
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import { AetherPage } from '../components/AetherPage';
import PlanExpiredModal from '../components/PlanExpiredModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Inventory = () => {
  const { language } = useLanguage();
  const { isPlanExpired } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [showPlanExpired, setShowPlanExpired] = useState(false);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [savedUnits, setSavedUnits] = useState(['pcs', 'kg', 'packets', 'liters', 'boxes']);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showMovement, setShowMovement] = useState(null);
  const [showActivity, setShowActivity] = useState(null);
  const [showImageModal, setShowImageModal] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Summary data
  const [summary, setSummary] = useState({
    total_purchase_value: 0,
    total_selling_value: 0,
    quantities_by_unit: {},
    total_items: 0
  });
  const [selectedUnit, setSelectedUnit] = useState('all');
  
  // Activity log
  const [activityLog, setActivityLog] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Suppliers list
  const [suppliers, setSuppliers] = useState([]);
  
  const [movementData, setMovementData] = useState({ 
    change_amount: '', 
    movement_type: 'in', 
    reason: '',
    supplier_id: '',
    purchase_price: ''
  });
  const [formData, setFormData] = useState({
    item_name: '', sku: '', category: '', current_quantity: 0, unit: 'pcs',
    buying_price: 0, selling_price: 0, low_stock_threshold: 10, supplier_id: ''
  });
  
  // New category/unit input
  const [newCategory, setNewCategory] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showNewUnit, setShowNewUnit] = useState(false);
  
  // Image upload state
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch inventory summary
  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory/summary`, { withCredentials: true });
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  // API_URL and axios are module-level constants — safe to omit from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch items
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (search) params.append('search', search);
      if (filterCategory) params.append('category', filterCategory);
      if (showLowStock) params.append('low_stock', 'true');
      const res = await axios.get(`${API_URL}/api/inventory?${params}`, { withCredentials: true });
      setItems(res.data.items);
      setTotal(res.data.total);
      setLowStockCount(res.data.low_stock_count);
      setCategories(res.data.categories || []);
    } catch (err) {
      toast.error('Failed to load inventory');
    }
    setLoading(false);
  }, [page, search, filterCategory, showLowStock]);

  // Fetch categories and units
  const fetchCategoriesAndUnits = async () => {
    try {
      const [catRes, unitRes] = await Promise.all([
        axios.get(`${API_URL}/api/inventory/categories`, { withCredentials: true }),
        axios.get(`${API_URL}/api/inventory/units`, { withCredentials: true })
      ]);
      setCategories(catRes.data.categories || []);
      setSavedUnits(unitRes.data.units || ['pcs', 'kg', 'packets', 'liters', 'boxes']);
    } catch (err) {
      console.error('Failed to load categories/units');
    }
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/suppliers?per_page=100`, { withCredentials: true });
      setSuppliers(res.data.suppliers || []);
    } catch (err) {
      console.error('Failed to load suppliers');
    }
  };

  // Fetch stock activity for an item
  const fetchActivity = async (itemId) => {
    setLoadingActivity(true);
    try {
      const res = await axios.get(`${API_URL}/api/stock-movements/activity/${itemId}`, { withCredentials: true });
      setActivityLog(res.data.activity || []);
      setShowActivity(res.data.item);
    } catch (err) {
      toast.error('Failed to load activity');
    }
    setLoadingActivity(false);
  };

  useEffect(() => { 
    fetchItems(); 
    fetchSummary();
  }, [fetchItems, fetchSummary]);

  useEffect(() => {
    fetchCategoriesAndUnits();
    fetchSuppliers();
  }, []);

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'hi' ? 'केवल JPG, PNG, WebP छवियाँ' : 'Only JPG, PNG, WebP images allowed');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'hi' ? 'फ़ाइल 5MB से छोटी होनी चाहिए' : 'File must be smaller than 5MB');
      return;
    }
    
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  // Upload image for item
  const uploadImage = async (itemId) => {
    if (!selectedFile) return null;
    
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedFile);
      
      const res = await axios.post(
        `${API_URL}/api/images/upload/stock-item/${itemId}`,
        formDataUpload,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(`Image uploaded (${res.data.original_size_kb}KB → ${res.data.compressed_size_kb}KB)`);
      return res.data.image_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  // Delete image from item
  const deleteImage = async (itemId) => {
    try {
      await axios.delete(`${API_URL}/api/images/stock-item/${itemId}`, { withCredentials: true });
      toast.success(language === 'hi' ? 'छवि हटाई गई' : 'Image removed');
      fetchItems();
    } catch (err) {
      toast.error('Failed to remove image');
    }
  };

  // Save new category
  const saveCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await axios.post(`${API_URL}/api/inventory/categories`, { category: newCategory.trim() }, { withCredentials: true });
      setCategories([...categories, newCategory.trim()]);
      setFormData({ ...formData, category: newCategory.trim() });
      setNewCategory('');
      setShowNewCategory(false);
      toast.success('Category saved');
    } catch (err) {
      toast.error('Failed to save category');
    }
  };

  // Save new unit
  const saveUnit = async () => {
    if (!newUnit.trim()) return;
    try {
      await axios.post(`${API_URL}/api/inventory/units`, { unit: newUnit.trim().toLowerCase() }, { withCredentials: true });
      setSavedUnits([...savedUnits, newUnit.trim().toLowerCase()]);
      setFormData({ ...formData, unit: newUnit.trim().toLowerCase() });
      setNewUnit('');
      setShowNewUnit(false);
      toast.success('Unit saved');
    } catch (err) {
      toast.error('Failed to save unit');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.item_name.trim()) { toast.error('Item name is required'); return; }
    try {
      const payload = { 
        ...formData, 
        current_quantity: Number(formData.current_quantity), 
        buying_price: Number(formData.buying_price), 
        selling_price: Number(formData.selling_price), 
        low_stock_threshold: Number(formData.low_stock_threshold) 
      };
      
      let itemId;
      if (editingItem) {
        await axios.put(`${API_URL}/api/inventory/${editingItem.item_id}`, payload, { withCredentials: true });
        itemId = editingItem.item_id;
        toast.success(language === 'hi' ? 'आइटम अपडेट हुआ' : 'Item updated');
      } else {
        const res = await axios.post(`${API_URL}/api/inventory`, payload, { withCredentials: true });
        itemId = res.data.item_id;
        toast.success(language === 'hi' ? 'आइटम जोड़ा गया' : 'Item added');
        // Trigger onboarding completion if active
        if (window.triggerOnboardingComplete) window.triggerOnboardingComplete('stock');
      }
      
      if (selectedFile && itemId) {
        await uploadImage(itemId);
      }
      
      setShowForm(false);
      setEditingItem(null);
      resetForm();
      fetchItems();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleMovement = async (e) => {
    e.preventDefault();
    if (!movementData.change_amount || Number(movementData.change_amount) <= 0) {
      toast.error('Enter a valid quantity'); return;
    }
    try {
      const payload = {
        item_id: showMovement.item_id,
        change_amount: Number(movementData.change_amount),
        movement_type: movementData.movement_type,
        reason: movementData.reason || null,
      };
      
      // Add supplier info for stock in
      if (movementData.movement_type === 'in' && movementData.supplier_id) {
        payload.supplier_id = movementData.supplier_id;
        if (movementData.purchase_price) {
          payload.purchase_price = Number(movementData.purchase_price);
        }
      }
      
      await axios.post(`${API_URL}/api/stock-movements`, payload, { withCredentials: true });
      toast.success(movementData.movement_type === 'in' ? 'Stock added' : 'Stock removed');
      setShowMovement(null);
      setMovementData({ change_amount: '', movement_type: 'in', reason: '', supplier_id: '', purchase_price: '' });
      fetchItems();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'hi' ? 'क्या आप इसे हटाना चाहते हैं?' : 'Delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/api/inventory/${id}`, { withCredentials: true });
      toast.success('Item deleted');
      fetchItems();
      fetchSummary();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({ item_name: '', sku: '', category: '', current_quantity: 0, unit: 'pcs', buying_price: 0, selling_price: 0, low_stock_threshold: 10, supplier_id: '' });
    setImagePreview(null);
    setSelectedFile(null);
    setShowNewCategory(false);
    setShowNewUnit(false);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ 
      item_name: item.item_name, 
      sku: item.sku || '', 
      category: item.category || '', 
      current_quantity: item.current_quantity, 
      unit: item.unit, 
      buying_price: item.buying_price, 
      selling_price: item.selling_price, 
      low_stock_threshold: item.low_stock_threshold,
      supplier_id: item.supplier_id || ''
    });
    setImagePreview(item.image_url ? `${API_URL}${item.image_url}` : null);
    setSelectedFile(null);
    setShowForm(true);
  };

  const totalPages = Math.ceil(total / 20);

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_URL}${imageUrl}`;
  };

  // Format date to IST
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Get total quantity for selected unit
  const getQuantityForUnit = () => {
    if (selectedUnit === 'all') {
      return Object.entries(summary.quantities_by_unit)
        .map(([unit, qty]) => `${qty} ${unit}`)
        .join(', ') || '0';
    }
    return `${summary.quantities_by_unit[selectedUnit] || 0} ${selectedUnit}`;
  };

  const invBadge2 = lowStockCount > 0 ? (
    <span className="inline-flex items-center gap-1 bg-red-500/15 text-red-300 border border-red-500/30 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider" data-testid="low-stock-badge">
      <AlertTriangle className="h-3 w-3" />{lowStockCount} {language === 'hi' ? 'कम स्टॉक' : 'low stock'}
    </span>
  ) : null;

  const invAddBtn2 = (
    <Button onClick={() => {
      if (isPlanExpired()) { setShowPlanExpired(true); return; }
      setEditingItem(null); resetForm(); setShowForm(true);
    }} className="bg-[#021945] hover:bg-[#021945]/90" data-testid="add-item-btn">
      <Plus className="h-4 w-4 mr-1" />{language === 'hi' ? 'जोड़ें' : 'Add'}
    </Button>
  );

  return (
    <AetherPage
      title={language === 'hi' ? 'स्टॉक/इन्वेंटरी' : 'Stock/Inventory'}
      icon={Package}
      iconColor="#ffffff"
      titleTestId="inventory-title"
      badge={invBadge2}
      rightElement={invAddBtn2}
      mainClassName="p-4 md:p-8 space-y-6"
    >
          {/* Page intro */}
          <div className="hidden md:block">
            <p className="label-caps text-white/55 mb-1">{language === 'hi' ? 'स्टॉक मॉनिटर' : 'INVENTORY MONITOR'}</p>
            <p className="text-white/55 text-sm">{language === 'hi' ? 'खरीद, बिक्री मूल्य और कुल मात्रा की रीयल-टाइम स्थिति।' : 'Real-time view of purchase value, selling value, and total quantity across all SKUs.'}</p>
          </div>

          {/* Summary KPIs - Aether style (Stock theme) */}
          <div className="grid grid-cols-3 gap-2 md:gap-6">
            <div className="glass-card glass-card-hoverable p-3 md:p-6 relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/70 rounded-l-xl" />
              <div className="flex justify-between items-start mb-2 md:mb-4">
                <span className="label-caps text-white/55 text-[10px] md:text-[12px]">{language === 'hi' ? 'खरीद मूल्य' : 'Purchase Value'}</span>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-blue-500/10 hidden md:flex items-center justify-center">
                  <IndianRupee className="h-4 w-4 md:h-5 md:w-5 text-blue-400" />
                </div>
              </div>
              <p className="text-base md:text-3xl font-bold tracking-tight text-white" data-testid="purchase-value">
                ₹{summary.total_purchase_value?.toLocaleString('en-IN') || 0}
              </p>
              <p className="hidden md:block text-xs text-white/45 mt-1">{language === 'hi' ? 'मौजूदा इन्वेंटरी की लागत' : 'Total cost of current inventory'}</p>
            </div>

            <div className="glass-card glass-card-hoverable p-3 md:p-6 relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-[#62FF96]/70 rounded-l-xl" />
              <div className="flex justify-between items-start mb-2 md:mb-4">
                <span className="label-caps text-white/55 text-[10px] md:text-[12px]">{language === 'hi' ? 'बिक्री मूल्य' : 'Selling Value'}</span>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-[#62FF96]/10 hidden md:flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-[#62FF96]" />
                </div>
              </div>
              <p className="text-base md:text-3xl font-bold tracking-tight text-white" data-testid="selling-value">
                ₹{summary.total_selling_value?.toLocaleString('en-IN') || 0}
              </p>
              <p className="hidden md:block text-xs text-white/45 mt-1">{language === 'hi' ? 'अनुमानित कुल राजस्व' : 'Projected total revenue'}</p>
            </div>

            <div className="glass-card glass-card-hoverable p-3 md:p-6 relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-purple-400/70 rounded-l-xl" />
              <div className="flex justify-between items-start mb-2 md:mb-4">
                <span className="label-caps text-white/55 text-[10px] md:text-[12px]">{language === 'hi' ? 'कुल मात्रा' : 'Quantity'}</span>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-purple-500/10 hidden md:flex items-center justify-center">
                  <Layers className="h-4 w-4 md:h-5 md:w-5 text-purple-400" />
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2 mb-1">
                <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                  <SelectTrigger className="h-7 w-28 text-xs bg-[var(--aether-surface)] border-[var(--aether-outline-variant)] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-[#262626] text-white">
                    <SelectItem value="all">{language === 'hi' ? 'सभी' : 'All Units'}</SelectItem>
                    {Object.keys(summary.quantities_by_unit).map(unit => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-base md:text-xl font-bold tracking-tight text-white truncate" data-testid="total-quantity">
                {getQuantityForUnit()}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder={language === 'hi' ? 'खोजें...' : 'Search items...'} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-10" data-testid="inventory-search-input" />
            </div>
            <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder={language === 'hi' ? 'श्रेणी' : 'Category'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'hi' ? 'सभी श्रेणी' : 'All Categories'}</SelectItem>
                {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant={showLowStock ? 'default' : 'outline'} onClick={() => { setShowLowStock(!showLowStock); setPage(1); }} className="" data-testid="filter-low-stock-btn">
              <AlertTriangle className="h-4 w-4 mr-1" />{language === 'hi' ? 'कम स्टॉक' : 'Low Stock'}
            </Button>
          </div>

          {/* Item List */}
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-[#021945] border-t-transparent rounded-full" /></div>
          ) : items.length === 0 ? (
            <Card className="text-center py-12"><CardContent><Package className="h-12 w-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">{language === 'hi' ? 'कोई आइटम नहीं' : 'No items found'}</p></CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="inventory-list">
              {items.map((item) => {
                const isLow = item.current_quantity <= item.low_stock_threshold;
                const imageUrl = getImageUrl(item.image_url);
                return (
                  <Card key={item.item_id} className={`hover:shadow-md transition-shadow ${isLow ? 'border-red-200' : ''}`} data-testid={`inventory-card-${item.item_id}`}>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                        {/* Top row on mobile: Image + Name + Action Buttons */}
                        <div className="flex items-start gap-3 w-full sm:w-auto">
                          {/* Item Image - Clickable for expansion */}
                          <div 
                            className="w-12 h-12 sm:w-16 sm:h-16 flex-shrink-0 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center cursor-pointer"
                            onClick={() => imageUrl && setShowImageModal({ url: imageUrl, name: item.item_name })}
                          >
                            {imageUrl ? (
                              <img src={imageUrl} alt={item.item_name} className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <Package className="h-5 w-5 sm:h-6 sm:w-6 text-slate-300" />
                            )}
                          </div>
                          
                          {/* Item name and badges */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 sm:gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold text-sm sm:text-base text-slate-900 font-manrope truncate max-w-[120px] sm:max-w-none">{item.item_name}</h3>
                              {item.category && <Badge variant="secondary" className="text-[10px] sm:text-xs">{item.category}</Badge>}
                              {isLow && <Badge variant="destructive" className="text-[10px] sm:text-xs">{language === 'hi' ? 'कम' : 'Low'}</Badge>}
                            </div>
                          </div>
                          
                          {/* Action buttons - visible on mobile in top row */}
                          <div className="flex items-center gap-0.5 sm:hidden flex-shrink-0">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => fetchActivity(item.item_id)} title="Activity">
                              <History className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { setShowMovement(item); setMovementData({ change_amount: '', movement_type: 'in', reason: '', supplier_id: '', purchase_price: '' }); }} title="Stock">
                              <ArrowUp className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(item)}><Edit2 className="h-4 w-4 text-slate-500" /></Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(item.item_id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                          </div>
                        </div>
                        
                        {/* Stats grid */}
                        <div className="flex-1 min-w-0">
                          <div className="grid grid-cols-4 gap-1 sm:gap-2 text-xs sm:text-sm">
                            <div>
                              <span className="text-slate-400 text-[10px] sm:text-xs">{language === 'hi' ? 'स्टॉक' : 'Stock'}</span>
                              <p className={`font-medium ${isLow ? 'text-red-600' : 'text-slate-900'}`}>{item.current_quantity} {item.unit}</p>
                            </div>
                            <div>
                              <span className="text-slate-400 text-[10px] sm:text-xs">{language === 'hi' ? 'खरीद' : 'Buy'}</span>
                              <p className="text-slate-900">₹{item.buying_price ?? '—'}</p>
                            </div>
                            <div>
                              <span className="text-slate-400 text-[10px] sm:text-xs">{language === 'hi' ? 'बिक्री' : 'Sell'}</span>
                              <p className="text-slate-900">₹{item.selling_price ?? '—'}</p>
                            </div>
                            <div>
                              <span className="text-slate-400 text-[10px] sm:text-xs">{language === 'hi' ? 'मूल्य' : 'Value'}</span>
                              <p className="text-green-600 font-medium">₹{((item.total_value || ((item.selling_price ?? 0) * item.current_quantity)) || 0).toLocaleString('en-IN')}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Action buttons - Desktop only */}
                        <div className="hidden sm:flex items-center gap-1 ml-2 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => fetchActivity(item.item_id)} title="View Activity" data-testid={`activity-${item.item_id}`}>
                            <History className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setShowMovement(item); setMovementData({ change_amount: '', movement_type: 'in', reason: '', supplier_id: '', purchase_price: '' }); }} title="Stock In/Out" data-testid={`stock-movement-${item.item_id}`}>
                            <ArrowUp className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`edit-item-${item.item_id}`}><Edit2 className="h-4 w-4 text-slate-500" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(item.item_id)} data-testid={`delete-item-${item.item_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 pt-4">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="flex items-center text-sm text-slate-600">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}

      {/* Add/Edit Item Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-manrope">{editingItem ? (language === 'hi' ? 'आइटम अपडेट' : 'Edit Item') : (language === 'hi' ? 'नया आइटम' : 'Add Item')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Image Upload Section */}
            <div className="space-y-2">
              <label className="text-xs text-slate-500">{language === 'hi' ? 'आइटम छवि' : 'Item Image'}</label>
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center relative border-2 border-dashed border-slate-300">
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => { setImagePreview(null); setSelectedFile(null); if (editingItem?.image_url) deleteImage(editingItem.item_id); }} className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600">
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </>
                  ) : (
                    <ImageIcon className="h-8 w-8 text-slate-300" />
                  )}
                </div>
                <div className="flex-1">
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageSelect} />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="w-full" disabled={uploadingImage}>
                    {uploadingImage ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Uploading...</> : <><Upload className="h-4 w-4 mr-1" />Upload</>}
                  </Button>
                  <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP · Max 5MB</p>
                </div>
              </div>
            </div>

            <Input placeholder={language === 'hi' ? 'आइटम का नाम *' : 'Item Name *'} value={formData.item_name} onChange={(e) => setFormData({ ...formData, item_name: e.target.value })} data-testid="item-name-input" className="" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="SKU" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} className="" />
              
              {/* Category with Add New */}
              <div className="space-y-1">
                {showNewCategory ? (
                  <div className="flex gap-1">
                    <Input placeholder="New category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} className="text-sm" />
                    <Button type="button" size="sm" onClick={saveCategory}>Save</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setShowNewCategory(false); setNewCategory(''); }}>×</Button>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Category" /></SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowNewCategory(true)}>+</Button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">{language === 'hi' ? 'मात्रा' : 'Quantity'}</label><Input type="number" value={formData.current_quantity} onChange={(e) => setFormData({ ...formData, current_quantity: e.target.value })} className="" /></div>
              
              {/* Unit with Add New */}
              <div className="space-y-1">
                <label className="text-xs text-slate-500">{language === 'hi' ? 'इकाई' : 'Unit'}</label>
                {showNewUnit ? (
                  <div className="flex gap-1">
                    <Input placeholder="New unit" value={newUnit} onChange={(e) => setNewUnit(e.target.value)} className="text-sm" />
                    <Button type="button" size="sm" onClick={saveUnit}>Save</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setShowNewUnit(false); setNewUnit(''); }}>×</Button>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                      <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {savedUnits.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowNewUnit(true)}>+</Button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">{language === 'hi' ? 'खरीद मूल्य (₹)' : 'Buy Price (₹)'}</label><Input type="number" value={formData.buying_price} onChange={(e) => setFormData({ ...formData, buying_price: e.target.value })} className="" /></div>
              <div><label className="text-xs text-slate-500">{language === 'hi' ? 'बिक्री मूल्य (₹)' : 'Sell Price (₹)'}</label><Input type="number" value={formData.selling_price} onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })} className="" /></div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">{language === 'hi' ? 'कम स्टॉक सीमा' : 'Low Stock Threshold'}</label><Input type="number" value={formData.low_stock_threshold} onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })} className="" /></div>
              
              {/* Supplier Selection */}
              <div>
                <label className="text-xs text-slate-500">{language === 'hi' ? 'सप्लायर' : 'Supplier'}</label>
                <Select value={formData.supplier_id} onValueChange={(v) => setFormData({ ...formData, supplier_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="">{language === 'hi' ? 'रद्द करें' : 'Cancel'}</Button>
              <Button type="submit" className="bg-[#021945] hover:bg-[#021945]/90" data-testid="item-submit-btn" disabled={uploadingImage}>
                {editingItem ? (language === 'hi' ? 'अपडेट' : 'Update') : (language === 'hi' ? 'जोड़ें' : 'Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Movement Dialog */}
      <Dialog open={!!showMovement} onOpenChange={(o) => !o && setShowMovement(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-manrope">{language === 'hi' ? 'स्टॉक इन/आउट' : 'Stock In/Out'} - {showMovement?.item_name}</DialogTitle></DialogHeader>
          <form onSubmit={handleMovement} className="space-y-3">
            <div className="flex gap-2">
              <Button type="button" variant={movementData.movement_type === 'in' ? 'default' : 'outline'} onClick={() => setMovementData({ ...movementData, movement_type: 'in' })} className="flex-1" data-testid="movement-in-btn">
                <ArrowDown className="h-4 w-4 mr-1" />{language === 'hi' ? 'स्टॉक इन' : 'Stock In'}
              </Button>
              <Button type="button" variant={movementData.movement_type === 'out' ? 'default' : 'outline'} onClick={() => setMovementData({ ...movementData, movement_type: 'out' })} className="flex-1" data-testid="movement-out-btn">
                <ArrowUp className="h-4 w-4 mr-1" />{language === 'hi' ? 'स्टॉक आउट' : 'Stock Out'}
              </Button>
            </div>
            <Input type="number" placeholder={language === 'hi' ? 'मात्रा' : 'Quantity'} value={movementData.change_amount} onChange={(e) => setMovementData({ ...movementData, change_amount: e.target.value })} data-testid="movement-quantity-input" className="" />
            
            {/* Show supplier and purchase price for Stock In */}
            {movementData.movement_type === 'in' && (
              <>
                <Select value={movementData.supplier_id} onValueChange={(v) => setMovementData({ ...movementData, supplier_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder={language === 'hi' ? 'सप्लायर चुनें' : 'Select Supplier'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No supplier</SelectItem>
                    {suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" placeholder={language === 'hi' ? 'खरीद मूल्य प्रति यूनिट' : 'Purchase price per unit'} value={movementData.purchase_price} onChange={(e) => setMovementData({ ...movementData, purchase_price: e.target.value })} className="" />
              </>
            )}
            
            <Input placeholder={language === 'hi' ? 'कारण (वैकल्पिक)' : 'Reason (optional)'} value={movementData.reason} onChange={(e) => setMovementData({ ...movementData, reason: e.target.value })} className="" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMovement(null)} className="">{language === 'hi' ? 'रद्द करें' : 'Cancel'}</Button>
              <Button type="submit" className="bg-[#021945] hover:bg-[#021945]/90" data-testid="movement-submit-btn">{language === 'hi' ? 'सहेजें' : 'Save'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Activity Dialog */}
      <Dialog open={!!showActivity} onOpenChange={(o) => !o && setShowActivity(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              {language === 'hi' ? 'स्टॉक गतिविधि' : 'Stock Activity'} - {showActivity?.item_name}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="activity">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">{language === 'hi' ? 'विवरण' : 'Details'}</TabsTrigger>
              <TabsTrigger value="activity">{language === 'hi' ? 'गतिविधि' : 'Activity'}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="space-y-3">
              {showActivity && (
                <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm"><span className="text-slate-500">Current Stock:</span> <span className="font-medium">{showActivity.current_quantity} {showActivity.unit}</span></p>
                  <p className="text-sm"><span className="text-slate-500">Buy Price:</span> <span className="font-medium">₹{showActivity.buying_price ?? '—'}</span></p>
                  <p className="text-sm"><span className="text-slate-500">Sell Price:</span> <span className="font-medium">₹{showActivity.selling_price ?? '—'}</span></p>
                  <p className="text-sm"><span className="text-slate-500">Total Value:</span> <span className="font-medium text-green-600">₹{((showActivity.selling_price ?? 0) * showActivity.current_quantity).toLocaleString('en-IN')}</span></p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="activity" className="space-y-3">
              {loadingActivity ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : activityLog.length === 0 ? (
                <p className="text-center text-slate-500 py-8">{language === 'hi' ? 'कोई गतिविधि नहीं' : 'No activity recorded'}</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {activityLog.map((log, idx) => (
                    <div key={log.movement_id || idx} className="flex items-start gap-3 p-2 bg-slate-50 rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${log.movement_type === 'in' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {log.movement_type === 'in' ? <ArrowDown className="h-4 w-4 text-green-600" /> : <ArrowUp className="h-4 w-4 text-red-600" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {log.change_amount} {showActivity?.unit} {log.movement_type === 'in' ? 'added' : 'removed'}
                        </p>
                        <p className="text-xs text-slate-500">{log.details || log.reason || 'Manual adjustment'}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatDate(log.created_at)}</p>
                        {log.new_stock !== undefined && (
                          <p className="text-xs text-slate-500">New Stock: {log.new_stock} {showActivity?.unit}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Image Expansion Modal */}
      <Dialog open={!!showImageModal} onOpenChange={() => setShowImageModal(null)}>
        <DialogContent className="sm:max-w-lg p-2">
          <div className="relative aspect-square w-full bg-black rounded-lg overflow-hidden flex items-center justify-center">
            {showImageModal && (
              <img 
                src={showImageModal.url} 
                alt={showImageModal.name} 
                className="max-w-full max-h-full object-contain"
              />
            )}
          </div>
          <p className="text-center text-sm text-slate-500 mt-2">{showImageModal?.name}</p>
        </DialogContent>
      </Dialog>

      <PlanExpiredModal open={showPlanExpired} onClose={() => setShowPlanExpired(false)} />
    </AetherPage>
  );
};

export default Inventory;
